(function (upp) {
    'use strict';

    var URL_API_REST_SERVER = $UPP.URL_UPPLICATION_REST_SERVICE;
    var KEY_LAST_TIME_READ = 'notification_last_read';
    var KEY_TIME_START = 'notification_time_start';
    var DEVICE_TYPE = $UPP.IOS ? 2 : ($UPP.ANDROID ? 1 : 0);

    /**
     * @typedef {Obejct} Notification
     * @property {number} id
     * @property {string} alert
     * @property {number} created - Timestamp of the creation date for this push
     * @property {number} deviceType
     */

    /**
     * Service for interacting with the notifications api.
     * @class NotificationsService
     */
    NotificationsService.$inject = [ '$q', 'appi', 'storage' ]
    function NotificationsService($q, appi, storageService) {
        Object.defineProperties(this, {
            /**
             * @private
             * @property {external:ng.$q} NotificationsService#_$q
             */
            _$q: { value: $q },
            /**
             * @private
             * @property {appi} NotificationsService#_appi
             */
            _appi: { value: appi },
            /**
             * Represents the "platform" where the childupp is running at the moment.
             * @private
             * @property {number} NotificationsService#_deviceType
             */
            _deviceType: { value: DEVICE_TYPE },
            /**
             * Last time notifications were read by the user. This value is persisted
             * between executions.
             * @private
             * @property {Date} NotificationsService#_readAt
             */
            _readAt: {
                set: function(val) {
                    storageService.set(KEY_LAST_TIME_READ, (new Date(val)).toISOString());
                },
                get: function() {
                    if (!storageService.get(KEY_LAST_TIME_READ))
                        storageService.set(KEY_LAST_TIME_READ, (new Date()).toISOString());
                    return new Date(storageService.get(KEY_LAST_TIME_READ));
                },
            },
            /**
             * First time notifications were retrieved on the current childupp.
             * @private
             * @property {Date} NotificationsService#_startedAt
             */
            _startedAt: {
                set: function(val) {
                    storageService.set(KEY_TIME_START, (new Date(val)).toISOString());
                },
                get: function() {
                    if (!storageService.get(KEY_TIME_START))
                        storageService.set(KEY_TIME_START, (new Date()).toISOString());
                    return new Date(storageService.get(KEY_TIME_START));
                },
            },
            /**
             * Id of the currently logged in user.
             * @private
             * @property {Promise<number|null>} NotificationsService#_userId
             */
            _userId: {
                get: function() {
                    return appi.security.getAuthentication()
                    .then(function(auth) {
                        if (auth.type != 'anonymous')
                            return auth.principal.userId;
                        else
                            return null;
                    });
                },
            },
            /**
             * Date at wich the user was registered(actually activated), null if no
             * user.
             * @private
             * @property {Promise<Date|null>}
             */
            _registeredAt: {
                get: function() {
                    return appi.security.getAuthentication()
                    .then(function(auth) {
                        if (auth.type != 'anonymous' && auth.principal.registeredAt)
                            return new Date(auth.principal.registeredAt);
                        else
                            return null;
                    });
                }
            }
        });
    }

    /**
     * Queries the server for notifications sent towards the current
     * childupp instance.
     *
     * By default, this method:
     *  - only includes notifications targeted for everyone.
     *  - only includes notifications sent after the app install OR
     *      the user registration date, whichever is older.
     *
     * Said behaviour can be overriden with the query param.
     *
     * @private
     * @method NotificationsService#_queryNotifications
     * @param {Object} query
     * @param {string} query.since - Only notifications sent after this date
     *                             will be included. This value should be a valid
     *                             ISODate string.
     * @param {number} query.userId - Notifications sent explicitly for this
     *                              userId will be inlcuded on the result.
     * @return {Promise<Notification[]>}
     */
    NotificationsService.prototype._queryNotifications = function(query) {
        var url = [ URL_API_REST_SERVER, '/notifications', '/list' ]
            .map(function(part) { return part.replace(/^\/|\/$/ig, '')})
            .join('/');

        return this._$q.all([
            this._userId,
            this._registeredAt,
        ])
        .then(function(result) {
            var userId = result[0];
            var registeredAt = result[1];
            // Choose what date to use as "since", we pick the "older"
            var since = this._startedAt;
            if (registeredAt && registeredAt < this._startedAt)
                since = registeredAt;
            // Build the query params
            var params = Object.assign({}, {
                deviceType: this._deviceType,
                since: since,
                userId: userId || undefined,
            }, query);
            return this._appi.http.get(url, { params: params })
        }.bind(this))
        .then(function(response) {
            var notifications = response.data.notifications
            Object.defineProperty(notifications, 'pagination', {
                value: response.data.pagination,
            });
            return notifications;
        }.bind(this));
    }

    /**
     * Returns all the notifications sent towards the current childupp
     * instance and marks them as read, causing them to be excluded from
     * the value of {@link NotificationsService#getUnreadCount}
     *
     * @public
     * @method NotificationsService#getNotifications
     * @return {Promise<Notification[]>}
     */
    NotificationsService.prototype.getNotifications = function() {
        return this._queryNotifications()
        .then(function(notifications) {
            this._readAt = Date.now() // "Consume" the notifications
            return notifications;
        }.bind(this));
    }

    /**
     * Returns the amount of notifications pending to be read by the current
     * childupp instance.
     *
     * @public
     * @method NotificationsService#getUnreadCount
     * @return {Promise<number>}
     */
    NotificationsService.prototype.getUnreadCount = function() {
        return this._queryNotifications({ since: this._readAt })
        .then(function(notifications) {
            return notifications.length;
        }.bind(this));
    }

    /**
     * Finds an returns all the HTTP urls, HTTPS urls and emails present on the
     * given text.
     * @param {string} text
     * @return {string[]}
     */
    var _findUrls = function (text) {
        var source = (text || '').toString();
        var result = [];
        // see: https://regex101.com/r/vF7sT0/9 tests: 3 failing
        // we allow urls that start with // or http:// or https:// or www follow for any chars follow by a dot and letters and optional follow for any chars
        var regex = /((?:(?:(?:http(?:s?):\/\/)|\/\/|www\.)(?:[a-z0-9\.\-_]+)\.[a-z]{2,}(?:[^\s]+)?)|(?:(?:mailto:)?[_.\w-]+@(?:[\w][\w\-]+\.)+[a-zA-Z]{2,3}))\b/ig;

        var matches;
        while ((matches = regex.exec(source)) !== null) {
            var url = matches[0];
            if (url.indexOf("//") == 0 && $UPP.PHONEGAP) {
                url = "http:" + url;
            }
            result.push(url);
        }

        return result;
    };

    /**
     * Parse the given message and return a string where all its links and
     * emails have been replaced with html `<a>` tags that allow the links
     * to work as links.
     *
     * @public
     * @method NotificationsService#parseMessage
     * @param {string} message
     * @return {String}
     */
    NotificationsService.prototype.parseMessage = function(message) {
        var links = _findUrls(message);
        links.forEach(function(link) {
            message = message.replace(link, '<a href="' + link + '">' + link + '</a>');
        });
        return message;
    }

    upp
    .service('notificationsService', NotificationsService)
})(upp);


// WEBPACK FOOTER //
// ./src/app/modules/notifications/services/notifications-service.js